import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import { useTranslate } from 'src/locales';

import FormProvider, { RHFTextField, RHFRadioGroup } from 'src/components/hook-form';
import ManualDepositHelp from './manual-deposit-help';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onCreate: (payload: any) => void;
};

const PAYMENT_METHODS = ['MONCASH', 'NATCASH', 'MANUAL'] as const;
type PaymentMethod = (typeof PAYMENT_METHODS)[number];

type FormValues = {
  paymentMethod: PaymentMethod;
  amount: number;
  receipt: File | null;
};

export default function DepositForm({ open, onClose, onCreate }: Props) {
  const { t } = useTranslate();

  const DepositSchema: Yup.ObjectSchema<FormValues> = Yup.object({
    paymentMethod: Yup.mixed<PaymentMethod>()
      .oneOf(PAYMENT_METHODS)
      .required(t('wallet.requiredField')),
    amount: Yup.number().typeError(t('wallet.requiredField')).required(t('wallet.requiredField')),
    receipt: Yup.mixed<File>()
      .nullable()
      .when('paymentMethod', {
        is: 'MANUAL',
        then: (s) => s.required(t('wallet.requiredField')),
        otherwise: (s) => s.nullable(),
      }) as unknown as Yup.MixedSchema<File | null>,
  }).required();

  const defaultValues: FormValues = {
    paymentMethod: 'MANUAL',
    amount: 50,
    receipt: null,
  };

  const methods = useForm<FormValues>({
    resolver: yupResolver<FormValues>(DepositSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    watch,
    control,
    reset,
    formState: { isSubmitting },
  } = methods;

  const method = watch('paymentMethod');

  const onSubmit = handleSubmit(async (data) => {
    try {
      onCreate(data);
      reset(defaultValues); // ✅ reset after submit
      onClose();
    } catch (error) {
      console.error(error);
    }
  });

  const handleClose = () => {
    reset(defaultValues); // ✅ also reset on manual close/cancel
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{t('wallet.deposit')}</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            <RHFRadioGroup
              row
              name="paymentMethod"
              options={[
                { label: 'Moncash', value: 'MONCASH' },
                { label: 'Natcash', value: 'NATCASH' },
                { label: t('wallet.manual'), value: 'MANUAL' },
              ]}
            />

            <RHFTextField name="amount" label={t('wallet.amount')} />

            {method === 'MANUAL' && (
              <>
                <ManualDepositHelp />
                <Controller
                  name="receipt"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Stack spacing={1}>
                      <Button variant="outlined" component="label">
                        {field.value
                          ? t('wallet.change_file') || 'Change file'
                          : t('wallet.upload') || 'Upload receipt'}
                        <input
                          hidden
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
                        />
                      </Button>

                      {field.value && (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {field.value.name}
                        </Typography>
                      )}

                      {!!fieldState.error && (
                        <FormHelperText error>{fieldState.error.message}</FormHelperText>
                      )}
                    </Stack>
                  )}
                />
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" variant="outlined" onClick={handleClose}>
            {t('wallet.cancel')}
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {t('wallet.deposit')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
